import React, { useState, useContext, useCallback } from 'react';
import useCommonStyles from '../../../../../commonStyles';
import { Grid } from '@mui/material';
import { Typography } from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import InputConfig from '../../../../../Components/InputConfig';
import { Modalv2Context } from '../../../../../Contexts/Modalv2Context';
import { checkValidURI, debounce } from '../../../../../utilities/helpers';

/**
 * @name ModalTpVastTags  The function `ModalTpVastTags` renders a list of available vastTag for a particular configuration, it also has options to create/delete the vastTag from the list.
 */
const ModalTpVastTags = ({
	selectedFloor,
	selectedDemand,
	selectedPlatform,
	tpVastDoc,
	updateTpVastDoc,
	setSelectedTpValues
}) => {
	const { updateModalContent } = useContext(Modalv2Context);
	const commonStyleClasses = useCommonStyles();
	const [vastTag, setVastTag] = useState('');
	const [vastTagArray, setVastTagArray] = useState(
		tpVastDoc[selectedFloor][selectedDemand][selectedPlatform]
	);

	/**
	 * @name debouncedUpdateTpVastDoc  This function uses debounce and updates the tpVastDoc object after some delay
	 * @param {Object} newTpVastObj -  updated object after the changed/updated vastTag
	 */
	function deboucedUpdateTpVastDoc(newTpVastObj) {
		try {
			updateTpVastDoc(newTpVastObj);
		} catch (err) {
			toast.error(err.message, { position: toast.POSITION.BOTTOM_RIGHT });
		}
	}

	const debouncedFunction = useCallback(debounce(deboucedUpdateTpVastDoc, 1000), []);

	/**
	 * @name onVastTagChange  This function updates the vastTagArray with the updated vastTag and use debounce to update it in tpVastDoc object.
	 * @param {String} updatedVastTag -  vastTag that is changed/updated.
	 * @param {Number} vastTagIndex - the position of updated vastTag in the array
	 */
	const onVastTagChange = (updatedVastTag, vastTagIndex) => {
		const newVastTagArray = [...vastTagArray];
		const newTpVastObj = structuredClone(tpVastDoc);
		newVastTagArray[vastTagIndex] = updatedVastTag;
		newTpVastObj[selectedFloor][selectedDemand][selectedPlatform] = newVastTagArray;
		setVastTagArray(newVastTagArray);
		debouncedFunction(newTpVastObj);
	};

	/**
	 * @name onDeleteClick  The function `onDeleteClick` deletes the vastTag from the array and updates it.
	 * @param {String} vastTagIndex -  vastTagIndex to be deleted
	 */
	const onDeleteClick = (vastTagIndex) => {
		const newData = structuredClone(tpVastDoc);
		const updatedVastTags = vastTagArray.filter((tag, index) => index !== vastTagIndex);
		newData[selectedFloor][selectedDemand][selectedPlatform] = updatedVastTags;
		setVastTagArray(updatedVastTags);

		if (updatedVastTags.length === 0) {
			setSelectedTpValues({ floor: '', demand: '', platform: '' });
			updateModalContent(null);
			delete newData[selectedFloor][selectedDemand][selectedPlatform];
			if (Object.keys(newData[selectedFloor][selectedDemand]).length === 0) {
				delete newData[selectedFloor][selectedDemand];
			}
			if (Object.keys(newData[selectedFloor]).length === 0) {
				delete newData[selectedFloor];
			}
		}
		updateTpVastDoc(newData);
		setVastTag('');
	};

	/**
	 * @name onAddClick  The function `onAddClick` insert the vastTag into the array and updates the tpVast object.
	 */
	const onAddClick = () => {
		try {
			checkValidURI(vastTag);
			let newData = structuredClone(tpVastDoc);
			const newVastTagArray = [...vastTagArray];
			newVastTagArray.push(vastTag);
			newData[selectedFloor][selectedDemand][selectedPlatform] = newVastTagArray;
			updateTpVastDoc(newData);
			setVastTagArray(newVastTagArray);
			setVastTag('');
		} catch (err) {
			toast.error(err.message, { position: toast.POSITION.BOTTOM_RIGHT });
		}
	};

	return (
		<Grid
			className={commonStyleClasses.modalContentStyle}
			container
			spacing={2}
			width="50% !important"
			height="40% !important"
		>
			<Grid xs={12}>
				<Typography
					className={commonStyleClasses.heading}
					fontWeight={600}
					variant="h4"
					style={{
						textAlign: 'center'
					}}
				>
					Vast Tags
				</Typography>
			</Grid>
			{Object.values(vastTagArray).map((vastTag, vastTagIndex) => (
				<>
					<Grid item xs={10}>
						<InputConfig
							isObjectEditor={false}
							editable={true}
							directValue={vastTag}
							updateDirectValue={(value) => onVastTagChange(value, vastTagIndex)}
							inputStyles={{ width: '100%', margin: '0.5rem 0' }}
							inputType="text"
						/>
					</Grid>
					<Grid item xs={2}>
						<DeleteIcon
							color="primary"
							fontSize="medium"
							className={commonStyleClasses.icon}
							style={{ margin: '2rem 0.5rem 0 0.5rem' }}
							onClick={() => onDeleteClick(vastTagIndex)}
						/>
					</Grid>
				</>
			))}
			<Grid item xs={10}>
				<InputConfig
					isObjectEditor={false}
					editable={true}
					directValue={vastTag}
					updateDirectValue={setVastTag}
					inputStyles={{ width: '100%', margin: '0.5rem 0' }}
					inputType="text"
				/>
			</Grid>
			<Grid item xs={2}>
				<AddCircleOutlineOutlinedIcon
					className={commonStyleClasses.icon}
					color="primary"
					fontSize="large"
					style={{ marginTop: '1.3rem' }}
					onClick={onAddClick}
				/>
			</Grid>
		</Grid>
	);
};

export default ModalTpVastTags;
